/**
 * Finding and fetching a document for the viewer page (R235, docs/specs/cloud/document-viewer.md). Each link is turned
 * into its stored file through what this person may read (Row Level Security, `breach_report_pdf`), and the file comes
 * straight from Storage under the person's own sign-in and the bucket's own policy. No signed address is made, so
 * there is none to show, copy or pass on.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { PHOTO_BUCKET, type DocumentRef } from '@teslyn/domain';
import { loadBreachReportPdf } from '../deliverables/breachReportApi.ts';
import { plainPortalError } from '../portal/api.ts';
import { registerPhotoPath } from '../safety/client-register-api.ts';

/** A stored file the viewer can fetch. */
export interface StoredDocument {
  readonly bucket: string;
  readonly path: string;
  readonly name: string;
}

/** Not this person's to read now: withdrawn, replaced, removed, or never theirs. Storage does not say which. */
export class DocumentGone extends Error {
  constructor() {
    super('The document is not available.');
    this.name = 'DocumentGone';
  }
}

type Row<T> = { data: T | null; error: { code?: string; message: string } | null };

async function one<T>(query: PromiseLike<unknown>): Promise<T | null> {
  const { data, error } = (await query) as Row<T>;
  if (error) throw new Error(plainPortalError(error));
  return data;
}

/** A photo's display size is always a JPEG, whatever the original was. */
const jpegName = (name: string): string => `${name.replace(/\.[a-z0-9]{1,5}$/i, '') || 'Photo'}.jpg`;

/** The stored file a link names, or null when this person may not read it now. */
export async function findDocument(sb: SupabaseClient, ref: DocumentRef): Promise<StoredDocument | null> {
  switch (ref.doc) {
    case 'report': {
      const r = await one<{ storage_path: string | null; file_name: string | null }>(
        sb.from('report_publication').select('storage_path, file_name').eq('report_id', ref.id).maybeSingle(),
      );
      return r?.storage_path
        ? { bucket: 'reports', path: r.storage_path, name: r.file_name || 'Report.pdf' }
        : null;
    }
    case 'breach-report': {
      const r = await loadBreachReportPdf(sb, ref.project);
      return r
        ? { bucket: r.bucket, path: r.object_path, name: r.file_name || 'Height breach report.pdf' }
        : null;
    }
    case 'fuel-docket': {
      const r = await one<{ bucket: string; object_path: string; file_name: string | null }>(
        sb
          .from('project_file')
          .select('bucket, object_path, file_name')
          .eq('kind', 'fuel_docket')
          .eq('record_id', ref.id)
          .maybeSingle(),
      );
      return r ? { bucket: r.bucket, path: r.object_path, name: r.file_name || 'Fuel docket' } : null;
    }
    case 'register-photo': {
      const path = registerPhotoPath(ref.org, ref.project, ref.id, 'display');
      return path ? { bucket: PHOTO_BUCKET, path, name: jpegName(ref.name) } : null;
    }
  }
}

/** The file's bytes, read as this person. Storage answers 400 or 404 for a file the policy refuses as well. */
export async function fetchDocument(sb: SupabaseClient, file: StoredDocument): Promise<Blob> {
  const { data, error } = await sb.storage.from(file.bucket).download(file.path);
  if (data) return data;
  const status = Number((error as { status?: unknown } | null)?.status ?? 0);
  if (status >= 400 && status < 500) throw new DocumentGone();
  throw new Error(error ? plainPortalError(error) : 'The document could not be fetched.');
}
